import React, { useEffect, useState } from 'react';
import { KeyRound, X } from 'lucide-react';
import {
  OPEN_API_TOKEN_SETTINGS_EVENT,
  clearApiToken,
  hasApiToken,
  openApiTokenSettings,
  saveApiToken,
} from '../../../services/apiAuth';
import { ModalShell } from './ModalShell';

/**
 * Sekcja „Klucz dostępu do zapisanych diagnoz” (modal ustawień).
 * Zapisany klucz NIGDY nie trafia do DOM — pokazujemy tylko stan „zapisany / brak”.
 */
export const ApiTokenModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [saved, setSaved] = useState<boolean>(() => hasApiToken());
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    if (saveApiToken(value)) {
      setValue('');
      setError(null);
      setSaved(true);
    } else {
      setError('Nie udało się zapisać klucza — wpisz niepusty klucz (i sprawdź, czy przeglądarka zezwala na zapis).');
    }
  };

  const handleClear = () => {
    if (clearApiToken()) {
      setSaved(false);
      setError(null);
    } else {
      setError('Nie udało się usunąć klucza.');
    }
  };

  // Portal do <body> (ModalShell): nagłówek ma backdrop-filter, a to czyni go ramką odniesienia
  // dla `position: fixed` — renderowany w nim modal był przycinany do wysokości nagłówka.
  return (
    <ModalShell labelledBy="api-token-title" onEscape={onClose} panelClassName="max-w-md p-6 gap-4">
      <div className="flex items-start justify-between gap-3">
        <h2 id="api-token-title" className="text-lg font-semibold flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-accent shrink-0" />
          Klucz dostępu do zapisanych diagnoz
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Zamknij"
          className="p-2 rounded-lg border border-brd bg-surface-card text-txt-secondary hover:text-txt-primary hover:bg-surface-subtle cursor-pointer shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-txt-secondary">
        Klucz otrzymujesz od administratora. Jest przechowywany tylko w tej przeglądarce i wysyłany
        wyłącznie do serwera aplikacji.
      </p>
      <p className="text-xs font-semibold" role="status">
        {saved ? 'Klucz zapisany' : 'Brak zapisanego klucza'}
      </p>
      <label className="flex flex-col gap-1 text-xs text-txt-secondary">
        Nowy klucz
        <input
          type="password"
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="px-3 py-2 rounded-lg border border-brd bg-surface-card text-sm text-txt-primary focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent-border"
        />
      </label>
      {error && <p className="text-xs text-[color:var(--error)]" role="alert">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={handleClear} disabled={!saved} className="px-4 py-2 text-xs rounded-lg border border-brd bg-surface-card text-txt-secondary hover:bg-surface-subtle cursor-pointer disabled:opacity-50">
          Usuń
        </button>
        {/* text-txt-onAccent: w dark --accent-strong to jasny emerald-300 — biały tekst był nieczytelny */}
        <button type="button" onClick={handleSave} className="px-4 py-2 text-xs font-bold rounded-lg bg-accent-strong text-txt-onAccent hover:brightness-110 cursor-pointer">
          Zapisz
        </button>
      </div>
    </ModalShell>
  );
};

/** Komunikat błędu 401 z przyciskiem otwierającym ustawienia klucza (do użycia przy błędach /api/diagnoses). */
export const ApiAuthErrorNotice: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-xs text-[color:var(--error)] flex flex-wrap items-center gap-2" role="alert">
    <span>{message}</span>
    <button type="button" onClick={openApiTokenSettings} className="underline font-semibold cursor-pointer">
      Otwórz ustawienia klucza
    </button>
  </div>
);

/** Hook: nasłuchuje zdarzenia otwarcia ustawień (np. z ApiAuthErrorNotice). */
export function useApiTokenSettingsOpener(): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_API_TOKEN_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_API_TOKEN_SETTINGS_EVENT, handler);
  }, []);
  return [open, setOpen];
}
